/**
 * Texnologik sxema (flowsheet) JSON sxemasi.
 *
 * Sxema = tugunlar (apparatlar) + oqimlar (streams: from → to). Har tugun o'z
 * kirish/chiqish oqimlarini oqimlar ro'yxatidan oladi. Retsikl — grafda sikl;
 * yechuvchi tear stream tanlab ketma-ket yaqinlashadi (solve.ts).
 *
 * Birliklar: mol/s, K, Pa, J/s. Komponentlar — pack moddalari (components.json).
 */
import { z } from 'zod';
import { I18nText, VariantParam } from '../scenario/schema';

const id = z.string().regex(/^[a-zA-Z][a-zA-Z0-9_-]*$/);
const num = z.union([z.number(), z.string().regex(/^\$[a-zA-Z_][a-zA-Z0-9_]*$/)]);

export const NodeSchema = z.discriminatedUnion('type', [
  /** Kirish oqimi (manba) */
  z.object({ type: z.literal('feed'), id, label: I18nText.optional(), flow: z.record(num), T: num, P: num }).strict(),
  /** Chiqish (mahsulot yoki purge) */
  z.object({ type: z.literal('product'), id, label: I18nText.optional() }).strict(),
  /** Aralashtirgich: adiabatik, P = min(kirishlar) */
  z.object({ type: z.literal('mixer'), id, label: I18nText.optional() }).strict(),
  /** Ajratgich: chiqish oqimi → ulush (yig'indisi 1) */
  z.object({ type: z.literal('splitter'), id, label: I18nText.optional(), fractions: z.record(num) }).strict(),
  /** Isitgich/sovitgich: chiqish harorati yoki issiqlik yuki (biri) */
  z.object({ type: z.literal('heater'), id, label: I18nText.optional(), outletT: num.optional(), duty: num.optional() }).strict(),
  /** Konversiyali reaktor: har reaksiya uchun stexiometriya, kalit komponent, konversiya, ΔH_r */
  z
    .object({
      type: z.literal('reactor'),
      id,
      label: I18nText.optional(),
      mode: z.enum(['isothermal', 'adiabatic']).default('isothermal'),
      /** isothermal uchun chiqish T (bo'lmasa kirish T) */
      T: num.optional(),
      reactions: z
        .array(
          z
            .object({
              id: z.string(),
              /** komponent → koeffitsient (reagent manfiy, mahsulot musbat) */
              stoich: z.record(z.number()),
              key: z.string(),
              /** kalit komponent konversiyasi, 0..1 */
              conversion: num,
              /** J/mol (kalit komponentning 1 mol i uchun, |ν_key| ga keltirilgan); manfiy — ekzotermik */
              dHr: num,
            })
            .strict(),
        )
        .min(1),
    })
    .strict(),
  /** Komponentli separator: har komponentning "top" ga ketadigan ulushi, qolgani "bottom" */
  z
    .object({
      type: z.literal('separator'),
      id,
      label: I18nText.optional(),
      split: z.record(num),
      outlets: z.object({ top: id, bottom: id }).strict(),
      /** Chiqish harorati (ikkala oqim uchun); bo'lmasa kirish T */
      T: num.optional(),
    })
    .strict(),
  /** Kinetik CSTR (reaksiyalar kutubxonadan yoki inline) */
  z
    .object({
      type: z.literal('cstr'),
      id,
      label: I18nText.optional(),
      reactions: z.array(z.union([z.string(), z.object({}).passthrough()])).min(1),
      /** m³ */
      volume: num,
      /** cooled: adiabatik balans + berilgan issiqlik yuki (duty, W; manfiy — sovitish) */
      mode: z.enum(['isothermal', 'adiabatic', 'cooled']).default('isothermal'),
      T: num.optional(),
      /** cooled: qat'iy issiqlik yuki, W (manfiy — sovitish) */
      duty: num.optional(),
      /** cooled: ko'ylak Q = UA·(Tj − T), UA — W/K, Tj — sovitgich harorati, K */
      UA: num.optional(),
      Tj: num.optional(),
      /** shu haroratdan oshsa reactor.runaway (critical) */
      maxT: num.optional(),
      /** katalizator yoshi, s (faollik pasayishi uchun) */
      catalystAge: num.optional(),
      /** boshlang'ich taxmin: reaksiya id → konversiya ulushi (ko'p turg'un holat tanlovi) */
      guess: z.record(num).optional(),
      guessT: num.optional(),
    })
    .strict(),
  /** Kinetik PFR */
  z
    .object({
      type: z.literal('pfr'),
      id,
      label: I18nText.optional(),
      reactions: z.array(z.union([z.string(), z.object({}).passthrough()])).min(1),
      volume: num,
      mode: z.enum(['isothermal', 'adiabatic']).default('isothermal'),
      T: num.optional(),
      catalystAge: num.optional(),
      steps: z.number().int().min(10).max(20000).default(200),
      profile: z.boolean().default(false),
    })
    .strict(),
  /** Qavatli adiabatik katalitik reaktor, oraliq sovitish bilan */
  z
    .object({
      type: z.literal('bed-reactor'),
      id,
      label: I18nText.optional(),
      reactions: z.array(z.union([z.string(), z.object({}).passthrough()])).min(1),
      beds: z.array(z.object({ volume: num, catalystAge: num.optional() }).strict()).min(1),
      /** kirish harorati (bo'lmasa oqim harorati) */
      inletT: num.optional(),
      /** qavatlar orasidagi sovitgich chiqish harorati (bitta yoki har oraliq uchun) */
      intercoolT: z.union([num, z.array(num)]),
      steps: z.number().int().min(10).max(20000).default(200),
    })
    .strict(),
  /** Ko'p kesimli kolonna (qisqa model): har kesim uchun komponent ulushlari, qoldiq — pastki mahsulot */
  z
    .object({
      type: z.literal('column'),
      id,
      label: I18nText.optional(),
      /** kolonna bosimi, Pa (qaynash haroratlari uchun); bo'lmasa kirish bosimi */
      P: num.optional(),
      cuts: z
        .array(
          z
            .object({
              outlet: id,
              /** komponent → shu kesimga ketadigan ulush (kirish bo'yicha) */
              split: z.record(num),
              /** kesim harorati; bo'lmasa Raoult bo'yicha qaynash harorati (Antoine'li komponentlar) */
              T: num.optional(),
            })
            .strict(),
        )
        .min(1),
      bottom: id,
      bottomT: num.optional(),
    })
    .strict(),
]);
export type FlowsheetNode = z.infer<typeof NodeSchema>;

export const StreamSchema = z
  .object({
    id,
    from: id,
    to: id,
    label: I18nText.optional(),
    /** Tear stream uchun boshlang'ich taxmin (bo'lmasa 0 sarf) */
    guess: z.object({ flow: z.record(z.number()), T: z.number().optional(), P: z.number().optional() }).strict().optional(),
  })
  .strict();
export type StreamDef = z.infer<typeof StreamSchema>;

export const FlowsheetSchema = z
  .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    version: z.number().int().min(1),
    title: I18nText,
    description: I18nText.optional(),
    /** Pack moddalari identifikatorlari */
    components: z.array(z.string()).min(1),
    /** Oqimlar fazasi (entalpiya uchun Cp): hozircha butun sxema uchun bitta */
    phase: z.enum(['gas', 'liquid']).default('gas'),
    /** Entalpiya bazasi, K */
    Tref: z.number().default(298.15),
    variant: z.record(VariantParam).default({}),
    nodes: z.array(NodeSchema).min(1),
    streams: z.array(StreamSchema).min(1),
    /** Tear stream'lar (bo'lmasa avtomatik tanlanadi) */
    tear: z.array(id).optional(),
    solver: z
      .object({
        maxIter: z.number().int().min(1).default(200),
        /** nisbiy tolerans (tear o'zgaruvchilari) */
        tol: z.number().positive().default(1e-6),
        wegstein: z.boolean().default(true),
        qMin: z.number().default(-5),
        qMax: z.number().default(0),
      })
      .strict()
      .default({}),
  })
  .strict();
export type Flowsheet = z.infer<typeof FlowsheetSchema>;

export function parseFlowsheet(json: unknown): Flowsheet {
  const f = FlowsheetSchema.parse(json);
  const nodeIds = new Set(f.nodes.map((n) => n.id));
  if (nodeIds.size !== f.nodes.length) throw new Error(`flowsheet ${f.id}: duplicate node ids`);
  const streamIds = new Set<string>();
  for (const s of f.streams) {
    if (streamIds.has(s.id)) throw new Error(`flowsheet ${f.id}: duplicate stream ${s.id}`);
    streamIds.add(s.id);
    if (!nodeIds.has(s.from)) throw new Error(`flowsheet ${f.id}: stream ${s.id} from unknown node ${s.from}`);
    if (!nodeIds.has(s.to)) throw new Error(`flowsheet ${f.id}: stream ${s.id} to unknown node ${s.to}`);
  }
  for (const n of f.nodes) {
    if (n.type === 'heater' && (n.outletT === undefined) === (n.duty === undefined)) throw new Error(`flowsheet ${f.id}: heater ${n.id} needs exactly one of outletT, duty`);
  }
  for (const t of f.tear ?? []) if (!streamIds.has(t)) throw new Error(`flowsheet ${f.id}: tear ${t} is not a stream`);
  return f;
}
